import React, { useEffect, useState } from 'react';
import Skeleton from '../common/Skeleton';
import { Check, Trash2, CircleMinus, CircleCheck, Edit2, Lock, Link as LinkIcon, Plus, Search } from 'lucide-react';
import Modal from '../common/Modal';
import Badge from '../common/Badge';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { isUniqueLocal } from '../../validation/uniqueness';
import Toast, { ToastMessage } from '../common/Toast';
import type { DualListItem } from '../audits/procedure-types/common/DualListSelector';
import linkService, { LinkedItem, ControlLinkType } from '../../services/linkService';
import controlRegisterService, { ControlRegister, ControlCategory, ControlScoreHistoryRow } from '../../services/controlRegisterService';
import {
    controlDeficiencyService,
    ControlDeficiency,
} from '../../services/controlDeficiencyService';
import apiClient from '../../services/apiService';
import extractApiError from '../../utils/apiError';
import ControlImplementationsTab from './ControlImplementationsTab';
import ScoreControlModal from './ScoreControlModal';
import ControlVisualization from './ControlVisualization';
import Pagination from '../common/Pagination';
import { confirmWarning, confirmActive, confirmDelete, swalPermission } from '../common/confirm';
import { statusVariant, severityBadgeCss } from '../../utils/statusVariant';
import { NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

type TabKey = 'edit' | 'canvas' | ControlLinkType | 'implementations' | 'test_results' | 'deficiencies';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'edit', label: 'Basic Details' },
    { key: 'canvas', label: 'Canvas' },
    { key: 'risks', label: 'Risks' },
    { key: 'procedures', label: 'Procedures' },
    { key: 'implementations', label: 'Implementations' },
    { key: 'test_results', label: 'Test Results' },
    { key: 'deficiencies', label: 'Deficiencies' },
];

const CONTROL_TYPE_OPTIONS = [
    { value: 'Preventive', label: 'Preventive' },
    { value: 'Detective', label: 'Detective' },
    { value: 'Corrective', label: 'Corrective' },
];

const ControlConfigModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    control: ControlRegister;
    categories?: ControlCategory[];
    onSaved?: () => void;
    canModify?: boolean;
    canApprove?: boolean;
    initialTab?: TabKey;
}> = ({ isOpen, onClose, control, categories = [], onSaved, canModify = true, canApprove = true, initialTab }) => {
    const { formatDate } = useEntitySettings();
    const fieldVh = useFieldValidation();
    const fieldVhReject = useFieldValidation();
    const [activeTab, setActiveTab] = useState<TabKey>(initialTab || 'canvas');

    // Edit tab state
    const [isEditing, setIsEditing] = useState(false);
    const [controlState, setControlState] = useState<ControlRegister>(control);
    const [formData, setFormData] = useState<Partial<ControlRegister>>({});
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [submittingForApproval, setSubmittingForApproval] = useState(false);
    const [approving, setApproving] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    // Link tabs state
    const [available, setAvailable] = useState<DualListItem[]>([]);
    const [selected, setSelected] = useState<DualListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState<number | null>(null);

    // Test Results tab state — server-side paginated + searchable
    const HISTORY_PAGE_SIZE = 10;
    const [history, setHistory] = useState<ControlScoreHistoryRow[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyPage, setHistoryPage] = useState(1);
    const [historySearch, setHistorySearch] = useState('');
    const [historySearchApplied, setHistorySearchApplied] = useState(''); // debounced
    const [historyNonce, setHistoryNonce] = useState(0); // bump to reload after ad-hoc score add/delete
    const [showScoreModal, setShowScoreModal] = useState(false);

    // Deficiencies tab state
    const [deficiencies, setDeficiencies] = useState<ControlDeficiency[]>([]);
    const [deficienciesLoading, setDeficienciesLoading] = useState(false);

    const [toast, setToast] = useState<ToastMessage | null>(null);

    useEffect(() => {
        if (isOpen) {
            setActiveTab(initialTab || 'canvas');
            setIsEditing(false);
            setControlState(control);
            setFormData(control);
            setHistoryPage(1);
            setHistorySearch('');
            setHistorySearchApplied('');
            fieldVh.reset();
            fieldVhReject.reset();
        }
    }, [isOpen, control.id, initialTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce the Test Results search box; reset to page 1 on a new query.
    useEffect(() => {
        const t = setTimeout(() => {
            setHistorySearchApplied(historySearch.trim());
            setHistoryPage(1);
        }, 350);
        return () => clearTimeout(t);
    }, [historySearch]);

    // Load Deficiencies when tab is opened
    useEffect(() => {
        if (!isOpen || activeTab !== 'deficiencies') return;
        let cancelled = false;
        (async () => {
            setDeficienciesLoading(true);
            try {
                const data = await controlDeficiencyService.listForControl(control.id);
                if (!cancelled) setDeficiencies(data);
            } catch (e: any) {
                if (!cancelled) setToast({ type: 'error', message: extractApiError(e, 'Failed to load deficiencies') });
            } finally {
                if (!cancelled) setDeficienciesLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, activeTab, control.id]);

    // Load Test Results history when tab is opened
    useEffect(() => {
        if (!isOpen || activeTab !== 'test_results') return;
        let cancelled = false;
        (async () => {
            setHistoryLoading(true);
            try {
                const res = await controlRegisterService.getScoreHistory(control.id, {
                    page: historyPage,
                    limit: HISTORY_PAGE_SIZE,
                    search: historySearchApplied,
                });
                if (!cancelled) { setHistory(res.rows); setHistoryTotal(res.total); }
            } catch (e: any) {
                if (!cancelled) setToast({ type: 'error', message: extractApiError(e, 'Failed to load test history') });
            } finally {
                if (!cancelled) setHistoryLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, activeTab, control.id, historyNonce, historyPage, historySearchApplied]);

    // Soft-delete an ad-hoc score row (program scores are rejected server-side).
    const handleDeleteScore = async (scoreId: number) => {
        if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
        const ok = await confirmDelete({ title: 'Remove ad-hoc score?', text: 'This score will no longer count toward the control net score.' });
        if (!ok) return;
        try {
            await controlRegisterService.deleteScoreHistory(control.id, scoreId);
            setToast({ type: 'success', message: 'Ad-hoc score removed' });
            setHistoryNonce(n => n + 1);
            onSaved?.(); // net score / trend / signal in the Register may change
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to delete score') });
        }
    };

    // Load link data for risks/procedures tabs
    useEffect(() => {
        if (!isOpen || activeTab === 'implementations' || activeTab === 'edit' || activeTab === 'test_results' || activeTab === 'deficiencies') return;
        let cancelled = false;
        const load = async () => {
            setLoading(true); setToast(null);
            try {
                let allItems: DualListItem[] = [];
                if (activeTab === 'risks') {
                    // Approved + active risks only (green-dot) via unified /dropdown.
                    const resp = await apiClient.get('/api/v1/rsk/risks/dropdown');
                    const items = (resp.data?.items ?? []) as Array<{ id: number; name: string; code?: string }>;
                    allItems = items.map(r => ({
                        id: r.id, title: r.code || r.name,
                        description: r.name,
                    }));
                } else {
                    // Approved (parent process APPROVED) + active procedures only.
                    const resp = await apiClient.get('/api/v1/gov/process-steps/dropdown');
                    const items = (resp.data?.items ?? []) as Array<{ id: number; name: string; code?: string }>;
                    allItems = items.map(s => ({
                        id: s.id,
                        title: s.name,
                        description: s.code || undefined,
                    }));
                }
                const linked: LinkedItem[] = await linkService.listForControl(
                    control.id, activeTab as ControlLinkType
                );
                const linkedItems: DualListItem[] = linked.map(l => ({
                    id: l.id,
                    title: l.code || (l.step_number != null ? `Step ${l.step_number}: ${l.name}` : l.name),
                    description: l.code ? l.name : l.description,
                }));
                if (cancelled) return;
                setAvailable(allItems);
                setSelected(linkedItems);
            } catch (e: any) {
                if (!cancelled) setToast({ type: 'error', message: extractApiError(e, 'Failed to load') });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [isOpen, activeTab, control.id]);

    const handleAddItem = async () => {
        if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
        if (selectedItemId === null) return;
        const item = available.find((a) => a.id === selectedItemId);
        if (!item) return;
        const alreadyAdded = selected.some((s) => s.id === selectedItemId);
        if (alreadyAdded) {
            setToast({ type: 'error', message: `${item.title} is already linked.` });
            return;
        }

        const newSelected = [...selected, item];
        setSelected(newSelected);
        setShowAddModal(false);
        setSelectedItemId(null);

        // Save the link to backend
        setSaving(true);
        setToast(null);
        try {
            await linkService.replaceForControl(
                control.id, activeTab as ControlLinkType,
                newSelected.map((s) => Number(s.id))
            );
            setToast({
                type: 'success',
                message: `${item.title} linked successfully.`,
            });
            if (onSaved) onSaved();
        } catch (e: any) {
            setSelected(selected); // Revert on error
            setToast({
                type: 'error',
                message: extractApiError(e, 'Failed to link item'),
            });
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveItem = async (itemId: number) => {
        if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
        const item = selected.find((s) => s.id === itemId);
        if (!item) return;

        const prev = selected;
        const newSelected = selected.filter((s) => s.id !== itemId);
        setSelected(newSelected);

        setSaving(true);
        setToast(null);
        try {
            await linkService.replaceForControl(
                control.id, activeTab as ControlLinkType,
                newSelected.map((s) => Number(s.id))
            );
            setToast({
                type: 'success',
                message: `${item.title} unlinked successfully.`,
            });
            if (onSaved) onSaved();
        } catch (e: any) {
            setSelected(prev); // Revert on error
            setToast({
                type: 'error',
                message: extractApiError(e, 'Failed to unlink item'),
            });
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
        // control_code is system-generated (assigned on approval) and shown
        // read-only — it is NOT in the edit form, so validating it as a required
        // field silently blocked Update on every not-yet-approved control.
        const allValid = fieldVh.validateAll([
            { name: 'control_name', code: 109, value: formData.control_name || '', required: true },
            { name: 'control_description', code: 110, value: formData.control_description || '' },
        ]);
        if (!allValid) return;
        const validation = controlRegisterService.validateData(formData, true);
        if (!validation.valid) {
            requestAnimationFrame(() => {
                const form = (e.currentTarget as HTMLFormElement) || document.getElementById('config-control-form');
                const firstInvalid = form?.querySelector<HTMLElement>('[aria-invalid="true"]');
                if (firstInvalid) {
                    firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    firstInvalid.focus({ preventScroll: true });
                }
            });
            return;
        }
        setSubmitting(true); setToast(null);
        try {
            await controlRegisterService.update(controlState.id, formData);
            setControlState({ ...controlState, ...formData } as ControlRegister);
            setIsEditing(false);
            setToast({ type: 'success', message: 'Control updated successfully.' });
            onSaved?.();
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Update failed' });
        } finally { setSubmitting(false); }
    };

    const handleSubmitForApproval = async () => {
        if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
        const ok = await confirmDelete({
            title: 'Submit for approval?',
            text: `"${controlState.control_name}" will be sent for approval.`,
            confirmText: 'Yes, Submit',
        });
        if (!ok) return;
        setSubmittingForApproval(true);
        try {
            await controlRegisterService.submit(controlState.id);
            setToast({ type: 'success', message: 'Submitted for approval.' });
            onSaved?.();
            onClose();
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Failed to submit' });
        } finally { setSubmittingForApproval(false); }
    };

    const handleApprove = async () => {
        if (!canApprove) { swalPermission('You do not have permission to approve this record.'); return; }
        const ok = await confirmDelete({
            title: 'Approve this control?',
            text: `"${controlState.control_name}" will be approved.`,
            confirmText: 'Yes, Approve',
        });
        if (!ok) return;
        setApproving(true);
        try {
            await controlRegisterService.approve(controlState.id);
            setToast({ type: 'success', message: 'Control approved.' });
            onSaved?.();
            onClose();
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Failed to approve' });
        } finally { setApproving(false); }
    };

    const handleRejectClick = () => {
        if (!canApprove) { swalPermission('You do not have permission to approve this record.'); return; }
        setRejectionReason(''); setShowRejectionModal(true);
    };

    const handleRejectConfirm = async () => {
        const rejectValid = fieldVhReject.validateAll([
            { name: 'rejection_reason', code: 110, value: rejectionReason, required: true },
        ]);
        if (!rejectValid) return;
        if (!rejectionReason.trim()) {
            setToast({ type: 'error', message: 'Rejection reason is required' });
            return;
        }
        setRejecting(true);
        try {
            await controlRegisterService.reject(controlState.id, rejectionReason);
            setShowRejectionModal(false);
            setToast({ type: 'success', message: 'Control rejected.' });
            onSaved?.();
            onClose();
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Failed to reject' });
        } finally { setRejecting(false); }
    };

    const handleDelete = async () => {
        if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
        if (controlState.is_active === 1) { setToast({ type: 'error', message: 'Inactivate this control before deleting it.' }); return; }
        const ok = await confirmDelete({
            title: 'Delete this control?',
            text: `"${controlState.control_name}" will be permanently removed. Audit history is preserved.`,
        });
        if (!ok) return;
        setDeleting(true);
        try {
            await controlRegisterService.delete(controlState.id);
            setToast({ type: 'delete', message: 'Control deleted successfully' });
            onSaved?.();
            onClose();
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Delete failed' });
        } finally { setDeleting(false); }
    };

    const handleToggleStatus = async () => {
        if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
        const next = controlState.is_active === 1 ? 0 : 1;
        const isActive = controlState.is_active === 1;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this control?' : 'Inactivate this control?',
            text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await controlRegisterService.toggleStatus(controlState.id);
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated' : 'Inactivated' });
            onSaved?.();
            // Keep the modal open briefly so the toast is visible before unmount.
            setTimeout(() => onClose(), 1200);
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Failed to update status' });
        } finally { setToggling(false); }
    };

    const categoryOptions = categories.map(cat => ({
        value: cat.id,
        label: `${cat.category_name} (${cat.category_code})`,
    }));

    const anyBusy = submitting || deleting || toggling || submittingForApproval || approving || rejecting || saving;
    // Approved records are frozen: no field edits, no toggle/delete. Unlock via the
    // status dot's Reopen action.
    const isApproved = (controlState.workflow_state ?? controlState.status) === 'approved';
    const isEditTab = activeTab === 'edit';
    const isLinkTab = activeTab === 'risks' || activeTab === 'procedures';
    const showToggle = controlRegisterService.canToggle(controlState);
    // Hide Delete while the control is active (deactivate first); workflow rules still apply.
    const showDelete = controlState.is_active !== 1 && controlRegisterService.canDelete(controlState);

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="lg" closeOnOverlayClick={false} contentClassName="app-modal-content--with-table">
                <Modal.Header>
                    {isApproved && isEditTab ? (
                        <>Viewing (locked) "<span className="item-highlight">{control.control_name}</span>"</>
                    ) : (
                        <>Control Configuration — {control.control_code} · {control.control_name}</>
                    )}
                </Modal.Header>

                <Modal.Body className="">
                    <div className="app-modal-tabs">
                        {TABS.map(t => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setActiveTab(t.key)}
                                className={`app-modal-tab${activeTab === t.key ? ' app-modal-tab--active' : ''}`}
                                data-testid={`TAB_control_config_${t.key}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {isEditTab && (
                        <form id="config-control-form" onSubmit={handleSubmit} className="grid grid-cols-2 gap-4 pt-4 w-full" data-testid="MOD_control_config_form">
                            {isApproved && (
                                <div className="approved-banner col-span-2" data-testid="BANNER_control_config_locked">
                                    <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                                </div>
                            )}
                            {/* System code (shown in the list) — read-only, never editable. */}
                            <FormControl.Input
                                label="Code"
                                value={(controlState as any).code || control.control_code || '—'}
                                disabled
                                data-testid="INP_control_config_code"
                            />
                            <ValidatedInput
                                label="Control Name"
                                required
                                name="control_name"
                                code={109}
                                vh={fieldVh}
                                value={formData.control_name || ''}
                                onChange={(e) => setFormData({ ...formData, control_name: e.target.value })}
                                onAfterBlur={(val) => {
                                    if (!val) return;
                                    const unique = isUniqueLocal(943, val, [], control.id);
                                    if (!unique) fieldVh.setError('control_name', 'Control name already exists');
                                }}
                                placeholder="e.g., Access Control Procedure"
                                disabled={!isEditing}
                                data-testid="INP_control_config_name"
                            />
                            <ValidatedTextarea
                                label="Control Description"
                                name="control_description"
                                code={110}
                                vh={fieldVh}
                                value={formData.control_description || ''}
                                onChange={(e) => setFormData({ ...formData, control_description: e.target.value })}
                                rows={6}
                                disabled={!isEditing}
                                className="col-span-2"
                                data-testid="INP_control_config_description"
                            />
                            <FormControl.SearchSelect
                                label="Control Category"
                                required
                                options={categoryOptions}
                                value={formData.category_id ?? null}
                                onChange={(v) => setFormData({ ...formData, category_id: v ? Number(v) : undefined })}
                                placeholder="-- Select Category --"
                                disabled={!isEditing}
                                data-testid="SEL_control_config_category"
                            />
                            <FormControl.SearchSelect
                                label="Control Type"
                                options={CONTROL_TYPE_OPTIONS}
                                value={formData.control_type || null}
                                onChange={(v) => setFormData({ ...formData, control_type: v ? String(v) : undefined })}
                                placeholder="-- Select Type --"
                                clearable
                                disabled={!isEditing}
                                data-testid="SEL_control_config_type"
                            />
                        </form>
                    )}

                    {!isEditTab && (
                        <>
                            {activeTab === 'canvas' ? (
                                <div
                                    className="!p-0 overflow-hidden"
                                    style={{ margin: '0 -24px -24px' }}
                                >
                                    <ControlVisualization control={controlState} canModify={canModify} />
                                </div>
                            ) : activeTab === 'implementations' ? (
                                <ControlImplementationsTab controlId={control.id} canModify={canModify} />
                            ) : activeTab === 'deficiencies' ? (
                                <div className="flex flex-col h-full">
                                    <div className="table-list flex-1 min-h-0">
                                        <div className="table-list-scroll">
                                            <table className="table-list-table">
                                                <thead>
                                                    <tr>
                                                        <th className="table-list-th">Detected On</th>
                                                        <th className="table-list-th">Source</th>
                                                        <th className="table-list-th">From Test</th>
                                                        <th className="table-list-th">Severity</th>
                                                        <th className="table-list-th">Description</th>
                                                        <th className="table-list-th">Raised By</th>
                                                        <th className="table-list-th table-list-cell--status">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody aria-busy={deficienciesLoading} aria-live="polite">
                                                    {deficienciesLoading && <Skeleton.TableRows cols={['xs', 'sm', 'sm', 'md', 'sm', 'pill', 'pill']} />}
                                                    {!deficienciesLoading && deficiencies.length === 0 && (
                                                        <tr>
                                                            <td colSpan={7} className="table-list-empty-cell">
                                                                No deficiencies raised on this control.
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {!deficienciesLoading && deficiencies.map(d => (
                                                        <tr key={d.id} className="table-list-row">
                                                            <td className="table-list-cell">
                                                                <span className="text-xs text-gray-700">
                                                                    {formatDate(d.detected_on)}
                                                                </span>
                                                            </td>
                                                            <td className="table-list-cell">
                                                                <span className="text-xs capitalize">{d.source.replace('_', '-')}</span>
                                                            </td>
                                                            <td className="table-list-cell table-list-cell--truncate" title={d.source_test_name || ''}>
                                                                {d.source_test_name || '—'}
                                                            </td>
                                                            <td className="table-list-cell">
                                                                <span className={severityBadgeCss(d.detected_severity)}>
                                                                    {d.detected_severity}
                                                                </span>
                                                            </td>
                                                            <td className="table-list-cell table-list-cell--truncate" title={d.description}>
                                                                {d.description}
                                                            </td>
                                                            <td className="table-list-cell">{d.created_by_name || '—'}</td>
                                                            <td className="table-list-cell">
                                                                <Badge variant={statusVariant(d.status)} size="sm">
                                                                    {d.status}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ) : activeTab === 'test_results' ? (
                                <div className="flex flex-col h-full">
                                    <div className="netbody-header">
                                        <FormControl.Input
                                            type="text"
                                            placeholder="Search geography, program, source, maker…"
                                            value={historySearch}
                                            onChange={e => setHistorySearch(e.target.value)}
                                            leadingIcon={<Search size={14} />}
                                            className="filter-search"
                                            data-testid="INP_control_score_search"
                                        />
                                        <div className="flex items-center gap-2 ml-auto">
                                            <Button
                                                variant="fill"
                                                icon={Plus}
                                                onClick={() => {
                                                    if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
                                                    setShowScoreModal(true);
                                                }}
                                                disabled={historyLoading || !canModify}
                                                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                                                className={!canModify ? 'opacity-50 cursor-not-allowed' : ''}
                                                data-testid="BTN_control_record_score"
                                            >
                                                Record Score
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="table-list flex-1 min-h-0">
                                        <div className="table-list-scroll">
                                            <table className="table-list-table">
                                                <thead>
                                                    <tr>
                                                        <th className="table-list-th">Scored On</th>
                                                        <th className="table-list-th">Source</th>
                                                        <th className="table-list-th">Test Program</th>
                                                        <th className="table-list-th">Geography</th>
                                                        <th className="table-list-th">Maker</th>
                                                        <th className="table-list-th">Efficiency</th>
                                                        <th className="table-list-th">Effectiveness</th>
                                                        <th className="table-list-th">Net Score</th>
                                                        <th className="table-list-th">Net Deficiency</th>
                                                        <th className="table-list-th">Remarks</th>
                                                        <th className="table-list-th"></th>
                                                    </tr>
                                                </thead>
                                                <tbody aria-busy={historyLoading} aria-live="polite">
                                                    {historyLoading && <Skeleton.TableRows cols={['xs', 'xs', 'md', 'sm', 'sm', 'xs', 'xs', 'xs', 'xs', 'md', 'xs']} />}
                                                    {!historyLoading && history.length === 0 && (
                                                        <tr>
                                                            <td colSpan={11} className="table-list-empty-cell">
                                                                {historySearchApplied
                                                                    ? `No scores match “${historySearchApplied}”.`
                                                                    : 'No scores yet for this control. Use “Record Score” to add one directly.'}
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {!historyLoading && history.map(row => (
                                                        <tr key={row.id} className="table-list-row">
                                                            <td className="table-list-cell">
                                                                <span className="text-xs text-gray-700">
                                                                    {formatDate(row.scored_on)}
                                                                </span>
                                                            </td>
                                                            <td className="table-list-cell">
                                                                {row.source === 'adhoc'
                                                                    ? <span className="status-badge xs info">Ad-hoc</span>
                                                                    : <span className="status-badge xs neutral">Program</span>}
                                                            </td>
                                                            <td className="table-list-cell">
                                                                <span className="table-list-cell-title">{row.test_name || '—'}</span>
                                                                {(row.start_date || row.end_date) && (
                                                                    <span className="table-list-cell-sub">
                                                                        {formatDate(row.start_date)} → {formatDate(row.end_date)}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="table-list-cell">{row.location_name || '—'}</td>
                                                            <td className="table-list-cell">{row.maker_name || '—'}</td>
                                                            <td className="table-list-cell">
                                                                <span className="font-semibold text-gray-700">
                                                                    {row.efficiency_score ?? '—'}
                                                                    {row.efficiency_score != null && <span className="text-xs text-gray-400 ml-1">/ 5</span>}
                                                                </span>
                                                            </td>
                                                            <td className="table-list-cell">
                                                                <span className="font-semibold text-gray-700">
                                                                    {row.effectiveness_score ?? '—'}
                                                                    {row.effectiveness_score != null && <span className="text-xs text-gray-400 ml-1">/ 5</span>}
                                                                </span>
                                                            </td>
                                                            <td className="table-list-cell">
                                                                {row.net_control_score != null ? (
                                                                    <span className="status-badge xs info">
                                                                        {row.net_control_score} / 25
                                                                    </span>
                                                                ) : '—'}
                                                            </td>
                                                            <td className="table-list-cell">
                                                                {row.net_control_deficiency != null ? (
                                                                    <span className="status-badge xs danger">
                                                                        {row.net_control_deficiency} / 25
                                                                    </span>
                                                                ) : '—'}
                                                            </td>
                                                            <td className="table-list-cell table-list-cell--truncate" title={row.remarks || ''}>
                                                                {row.remarks || '—'}
                                                            </td>
                                                            <td className="table-list-cell table-list-cell--actions">
                                                                {row.source === 'adhoc' && canModify && (
                                                                    <button
                                                                        type="button"
                                                                        className="table-list-action table-list-action--delete"
                                                                        title="Remove ad-hoc score"
                                                                        onClick={() => handleDeleteScore(row.id)}
                                                                        data-testid={`BTN_control_score_delete_${row.id}`}
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <Pagination
                                        page={historyPage}
                                        pageSize={HISTORY_PAGE_SIZE}
                                        totalRecords={historyTotal}
                                        onPageChange={setHistoryPage}
                                        recordLabel="scores"
                                    />
                                </div>
                            ) : (
                                <div className="flex flex-col h-full">
                                    <div className="netbody-header">
                                        <div className="flex items-center gap-2 ml-auto">
                                            <Button
                                                variant="fill"
                                                icon={LinkIcon}
                                                onClick={() => {
                                                    if (isApproved) { setToast({ type: 'error', message: 'Record is approved and locked. Reopen via the workflow button to edit.' }); return; }
                                                    if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
                                                    setShowAddModal(true);
                                                }}
                                                disabled={loading || (!canModify && !isApproved)}
                                                title={isApproved ? 'Record is approved and locked' : (!canModify ? NOT_AUTHORISED_MSG : undefined)}
                                                className={(!canModify || isApproved) ? 'opacity-50 cursor-not-allowed' : ''}
                                                data-testid="BTN_control_config_link"
                                            >
                                                Link
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="table-list flex-1 min-h-0">
                                        <div className="table-list-scroll">
                                            <table className="table-list-table">
                                                <thead>
                                                    <tr>
                                                        <th className="table-list-th">
                                                            {activeTab === 'risks' ? 'Risk Code' : 'Procedure'}
                                                        </th>
                                                        <th className="table-list-th">Description</th>
                                                        <th className="table-list-th"></th>
                                                    </tr>
                                                </thead>
                                                <tbody aria-busy={loading} aria-live="polite">
                                                    {loading && <Skeleton.TableRows cols={['sm', 'md']} />}
                                                    {!loading && selected.length === 0 && (
                                                        <tr>
                                                            <td colSpan={3} className="table-list-empty-cell">
                                                                No {activeTab} linked.
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {!loading &&
                                                        selected.map((item) => (
                                                            <tr
                                                                key={item.id}
                                                                className="table-list-row"
                                                            >
                                                                <td className="table-list-cell">
                                                                    <span className="table-list-cell-title">
                                                                        {item.title}
                                                                    </span>
                                                                </td>
                                                                <td
                                                                    className="table-list-cell table-list-cell--truncate"
                                                                    title={item.description || ''}
                                                                >
                                                                    {item.description || '—'}
                                                                </td>
                                                                <td className="table-list-cell table-list-cell--actions">
                                                                    {canModify && (
                                                                        <button
                                                                            type="button"
                                                                            className="table-list-action table-list-action--delete"
                                                                            title={isApproved ? 'Record is approved and locked' : `Unlink ${item.title}`}
                                                                            onClick={() => handleRemoveItem(Number(item.id))}
                                                                            disabled={saving || isApproved}
                                                                            data-testid={`BTN_control_config_unlink_${item.id}`}
                                                                        >
                                                                            <Trash2 size={14} />
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer className={isEditTab && !isApproved && (showToggle || showDelete) ? 'app-modal-footer--split' : ''}>
                    {isEditTab && !isApproved && (
                        <>
                            <div className="flex gap-2">
                                {showDelete && (
                                    <Button variant="delete-outline" icon={Trash2} onClick={handleDelete} disabled={anyBusy} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_control_config_delete">
                                        {deleting ? 'Deleting...' : 'Delete'}
                                    </Button>
                                )}
                                {showToggle && (
                                    <Button
                                        variant={controlState.is_active === 1 ? 'inactive-outline' : 'active-outline'}
                                        icon={controlState.is_active === 1 ? CircleMinus : CircleCheck}
                                        onClick={handleToggleStatus}
                                        disabled={anyBusy}
                                        className={!canModify ? 'opacity-50 cursor-not-allowed' : ''}
                                        data-testid="BTN_control_config_toggle_active"
                                    >
                                        {toggling ? '...' : controlState.is_active === 1 ? 'Inactive' : 'Active'}
                                    </Button>
                                )}
                            </div>
                            <div className="flex gap-2">
                                {isEditing ? (
                                    <>
                                        {controlRegisterService.canSubmit(controlState) && (
                                            <Button variant="outline" onClick={handleSubmitForApproval} disabled={anyBusy} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_control_config_submit_for_approval">
                                                {submittingForApproval ? 'Submitting...' : 'Submit for Approval'}
                                            </Button>
                                        )}
                                        {(controlState.workflow_state ?? controlState.status) === 'submitted' && (
                                            <Button variant="delete-outline" onClick={handleRejectClick} disabled={anyBusy} className={!canApprove ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_control_config_reject">
                                                {rejecting ? 'Rejecting...' : 'Reject'}
                                            </Button>
                                        )}
                                        {(controlState.workflow_state ?? controlState.status) === 'submitted' && (
                                            <Button variant="update" icon={Check} onClick={handleApprove} disabled={anyBusy} className={!canApprove ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_control_config_approve">
                                                {approving ? 'Approving...' : 'Approve'}
                                            </Button>
                                        )}
                                        <Button type="submit" form="config-control-form" variant="update" icon={Check} disabled={anyBusy} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_control_config_update">
                                            {submitting ? 'Updating...' : 'Update'}
                                        </Button>
                                    </>
                                ) : (
                                    <Button variant="fill" icon={Edit2} onClick={() => { if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; } setIsEditing(true); }} disabled={anyBusy || !canModify} title={!canModify ? NOT_AUTHORISED_MSG : undefined} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_control_config_edit">
                                        Edit
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Footer>

                <Toast toast={toast} onClose={() => setToast(null)} />
            </Modal>

            {/* Link modal */}
            {isLinkTab && (
                <Modal
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                        setSelectedItemId(null);
                    }}
                    size="sm"
                >
                    <Modal.Header>Link {activeTab}</Modal.Header>
                    <Modal.Body>
                        <form
                            id="add-link-form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleAddItem();
                            }}
                            className="flex flex-col gap-y-4"
                            data-testid="MOD_control_config_link_form"
                        >
                            <FormControl.SearchSelect
                                label={`Select ${activeTab}`}
                                options={available.map((a) => ({
                                    value: a.id,
                                    label: a.title,
                                }))}
                                value={selectedItemId}
                                onChange={(v) =>
                                    setSelectedItemId(v !== null ? Number(v) : null)
                                }
                                placeholder={`— Choose ${activeTab} —`}
                                required
                                data-testid="SEL_control_config_link_target"
                            />
                        </form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            type="submit"
                            form="add-link-form"
                            variant="fill"
                            icon={Check}
                            disabled={selectedItemId === null || saving}
                            data-testid="BTN_control_config_link_submit"
                        >
                            {saving ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Rejection sub-modal */}
            <Modal isOpen={showRejectionModal} onClose={() => setShowRejectionModal(false)} size="sm">
                <Modal.Header>
                    Reject "<span className="item-highlight">{controlState.control_name}</span>"
                </Modal.Header>
                <Modal.Body>
                    <p className="text-sm text-gray-600 mb-4">Please provide a reason for rejection:</p>
                    <ValidatedTextarea
                        label="Rejection Reason"
                        required
                        name="rejection_reason"
                        code={110}
                        vh={fieldVhReject}
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={4}
                        data-testid="INP_control_config_rejection_reason"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="fill" icon={Check} type="button" onClick={handleRejectConfirm}
                        disabled={!rejectionReason || rejectionReason.trim() === '' || rejecting}
                        data-testid="BTN_control_config_reject_confirm"
                    >
                        {rejecting ? 'Rejecting...' : 'Confirm Rejection'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Ad-hoc score sub-modal (Test Results tab → Record Score) */}
            <ScoreControlModal
                isOpen={showScoreModal}
                onClose={() => setShowScoreModal(false)}
                controlId={control.id}
                controlName={controlState.control_name}
                onSaved={() => {
                    setToast({ type: 'success', message: 'Score recorded' });
                    setHistoryNonce(n => n + 1);
                    onSaved?.(); // net score / trend / signal in the Register may change
                }}
            />
        </>
    );
};

export default ControlConfigModal;
